/**
 * Planlagt lunsj (owner, 2026-10-08): when a break is meant to be taken inside a shift. A plan
 * only: pay follows the company's break rule and the punches, never this. The suggestion is the
 * owner's: only for a shift over 5.5 hours, in its middle, the company's break length, and the
 * manager can move it or leave it out.
 */
import type { ApiShift } from '@/api/data';
import { oslo } from '@/rules';

const MIN = 60_000;

/** The planned lunch as wall-clock times, or null when none is planned. */
export function plannedLunch(s: ApiShift): { from: string; to: string; minutes: number } | null {
  if (!s.planned_lunch_start || !s.planned_lunch_minutes) return null;
  const from = oslo(s.planned_lunch_start)?.time ?? '';
  const end = new Date(Date.parse(s.planned_lunch_start) + s.planned_lunch_minutes * MIN).toISOString();
  return { from, to: oslo(end)?.time ?? '', minutes: s.planned_lunch_minutes };
}

/**
 * The suggested lunch for a shift with none planned: over 5.5 hours, centred, rounded to the
 * quarter hour, the company's break length. Null for a shorter shift or one already planned.
 */
export function suggestLunch(s: ApiShift, minutes: number): { start: string; minutes: number } | null {
  if (plannedLunch(s)) return null;
  const a = Date.parse(s.start_time);
  const b = Date.parse(s.end_time);
  if ((b - a) / 36e5 <= 5.5) return null;
  const q = 15 * MIN;
  const mid = a + (b - a) / 2 - (minutes * MIN) / 2;
  return { start: new Date(Math.round(mid / q) * q).toISOString(), minutes };
}

/** A planned lunch carried along when a shift moves by `deltaMs`; nothing when none is planned. */
export function movedLunch(s: ApiShift, deltaMs: number): { planned_lunch_start?: string } {
  if (!s.planned_lunch_start || !s.planned_lunch_minutes) return {};
  return { planned_lunch_start: new Date(Date.parse(s.planned_lunch_start) + deltaMs).toISOString() };
}
